import {type BotDifficulty, RoomSettingConstants} from '@colonist/game-base-types'
import {type TranslationObject} from '@colonist/i18n'
import {type RoomSessionClientState} from '../../RoomSessionClientState.ts'
import {type RoomClientState} from '../../RoomClientState.ts'
import {UIRoomUserView, type UserActionIcon} from './UIRoomUserView.ts'
import {clientI18n} from '../../ClientI18n.ts'
import {notificationController, socket} from '../../EntryShared.ts'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables.ts'
import {UIPopupController} from '../../ui_popup/UIPopupController.ts'
import {type ViewPopupOptions} from '../../ui_popup/ViewPopupOptions.ts'
import {setElementInnerHTML} from '../../ui_utils/DomUtils.ts'
import {UIBrowserTabController} from '../UIBrowserTabController.ts'
import {ClientUserState} from '../../ClientUserState.ts'
import {LobbyImage} from '@colonist/types'
import {type AjaxDataTypes} from '../../../../../shared/AjaxDataTypes.ts'
import {AnalyticEventCategory, AnalyticEventType, AnalyticEventValue, GiftMembershipMethod} from '../../../../../shared/entity_datas/EntityDatas.ts'
import {CA} from '../../analytics/Analytics.ts'
import {doActionIfCanGiftMembership, doActionIfLoggedIn} from '../../store/StoreUtils.ts'
import {navigateToPage} from '../../ui_utils/ClientUtils.ts'
import {getColonistUsername} from '../../ui_profile/UIUtilsProfile.ts'
import {UIUtilsFriends} from '../../ui_friends/UIUtilsFriends.ts'
import {UIViewProfilePopup} from '../../profile/UIViewProfilePopup.ts'
import {UIRoomExitConfirmationViewPopup} from './UIRoomExitConfirmationViewPopup.ts'
import {AudioManager, AudioType} from '@colonist/audio'

export abstract class UIRoomUserList {
    private readonly playerListContainer: HTMLDivElement
    private hostView: UIRoomUserView
    private readonly guestViews: UIRoomUserView[] = []
    private static readonly leaveSoundVolume = 0.6
    private static friendsData: AjaxDataTypes.UserFriendsData | undefined
    private roomState: RoomClientState | undefined

    constructor(playerListContainer: HTMLDivElement) {
        this.playerListContainer = playerListContainer

        ClientUserState.registerChangeListener(async() => {
            UIRoomUserList.friendsData = await UIUtilsFriends.getFriends(ClientUserState.userState)
            if(this.roomState == undefined) return
            this.update(this.roomState)
        })
    }

    protected abstract openEditProfileForPlayer(player: RoomSessionClientState | undefined): void

    clear() {
        setElementInnerHTML(this.playerListContainer, {key: 'strings:colonistCommon.utils.emptyString'})
        this.guestViews.splice(0, this.guestViews.length)
    }

    load(roomState: RoomClientState) {
        this.hostView = new UIRoomUserView(this.playerListContainer)
        this.createGuestViews()
        this.update(roomState)
    }

    update(newRoomState: RoomClientState) {
        this.roomState = newRoomState
        this.updateHostView(newRoomState)
        this.updateGuestSessionViews(newRoomState)
        this.updateBotButtonViews(newRoomState)
    }

    // ====================================================================================================================================
    // Views
    // ====================================================================================================================================

    private createGuestViews() {
        for(let i = 1; i < RoomSettingConstants.MAX_PLAYER_COUNT; i += 1) {
            const newView = new UIRoomUserView(this.playerListContainer)
            this.guestViews.push(newView)
        }
    }

    private updateHostView(roomState: RoomClientState) {
        this.hostView.update(roomState.hostSession)
        this.hostView.updateActionIcons(UIRoomUserList.getActionIconsToDisplay(roomState, roomState.hostSession))
        this.hostView.setUsername(UIRoomUserList.getUsername(roomState, roomState.hostSession))

        const hostClickAction = this.playerClickAction(roomState.hostSession, roomState.hostSession)

        UIRoomUserList.togglePlayerHover(this.hostView, hostClickAction == undefined)

        this.hostView.setOnclick(() => {
            AudioManager.play(AudioType.Click)
            hostClickAction?.()
        })

        const canEditColor = UIRoomUserList.shouldShowColorSelectorBackground(roomState, roomState.hostSession)
        this.updateColorSelector(canEditColor, this.hostView)
    }

    private updateGuestSessionViews(roomState: RoomClientState) {
        const guestSessions = roomState.getGuestSessions()
        for(const [index, view] of this.guestViews.entries()) {
            const guest = guestSessions[index]
            if(roomState.gameSetting.maxPlayers - 1 < index + 1) view.hideView()
            if(roomState.gameSetting.maxPlayers - 1 >= index + 1) view.showView()
            if(guest == undefined) {
                if(view.isShowingPlayer()) {
                    view.removePlayer()
                    UIBrowserTabController.blinkTab({key: 'strings:roomPage.left', options: {count: roomState.getSessionCount(), maxCount: roomState.gameSetting.maxPlayers}})
                    AudioManager.play(AudioType.LeaveRoom, UIRoomUserList.leaveSoundVolume)
                }
                continue
            }
            if(!view.isShowingPlayer()) {
                UIBrowserTabController.blinkTab({key: 'strings:roomPage.joined', options: {count: roomState.getSessionCount(), maxCount: roomState.gameSetting.maxPlayers}})
                AudioManager.play(AudioType.JoinRoom)
            }
            view.update(guest)

            const guestClickAction = this.playerClickAction(roomState.hostSession, guest)

            UIRoomUserList.togglePlayerHover(view, guestClickAction == undefined)

            view.setOnclick(() => {
                AudioManager.play(AudioType.Click)
                guestClickAction?.()
            })

            const canEditColor = UIRoomUserList.shouldShowColorSelectorBackground(roomState, guest)
            this.updateColorSelector(canEditColor, view)

            view.updateActionIcons(UIRoomUserList.getActionIconsToDisplay(roomState, guest))

            view.setBotDifficultySelectorVisibility(guest.isBot)
            view.setBotDifficultySelectorDisabled(!UIRoomUserList.isItMe(roomState.hostSession))
            view.setBotDifficultySelectorOnClick((selectedBotDifficulty: BotDifficulty) => {
                socket.roomClient.setBotDifficulty(guest.roomSessionId, selectedBotDifficulty)
            })

            view.setUsername(UIRoomUserList.getUsername(roomState, guest))
        }
    }

    private updateBotButtonViews(roomState: RoomClientState) {
        if(roomState.hostSession.userSessionId == socket.controller.session.id) {
            for(const view of this.guestViews) {
                view.displayBotButton()
            }
            return
        }
        for(const view of this.guestViews) {
            view.hideBotButton()
        }
    }

    private updateColorSelector(canEditColor: boolean, userView: UIRoomUserView) {
        userView.setColorOnclick(canEditColor ? () => this.openEditProfileForPlayer(userView.state) : () => undefined)
        userView.toggleColorSelector(canEditColor)
    }

    // ====================================================================================================================================
    // Button Checks
    // ====================================================================================================================================

    static isItMe(session: RoomSessionClientState): boolean {
        return socket.controller.session.id == session.userSessionId
    }

    private static isItCurrentUserId(session: RoomSessionClientState): boolean {
        return ClientUserState.userState?.id == session.userId
    }

    private static isItHost(roomState: RoomClientState, session: RoomSessionClientState): boolean {
        return roomState.hostSession.userSessionId == session.userSessionId
    }

    private static shouldShowColorSelectorBackground(roomState: RoomClientState, session: RoomSessionClientState): boolean {
        if(session.isBot && this.isItMe(roomState.hostSession)) return true
        return this.isItMe(session)
    }

    // ====================================================================================================================================
    // Button Actions
    // ====================================================================================================================================

    protected playerClickAction(hostSession: RoomSessionClientState, playerSession: RoomSessionClientState): (() => void) | undefined {
        const clickedOnBot = playerSession.isBot
        if(clickedOnBot) return undefined

        const clickedOnSelf = UIRoomUserList.isItMe(playerSession)
        if(clickedOnSelf) return () => this.openEditProfilePage(playerSession)

        const amIHost = UIRoomUserList.isItMe(hostSession)
        if(amIHost) return () => UIRoomUserList.showHostActionsPopup(playerSession)
        return () => UIRoomUserList.openProfilePage(playerSession)
    }

    private openEditProfilePage(playerSession: RoomSessionClientState) {
        doActionIfLoggedIn(() => this.openEditProfileForPlayer(playerSession))
    }

    private static kickPlayer(playerSession: RoomSessionClientState) {
        socket.roomClient.kickPlayer(playerSession.roomSessionId)
    }

    private static showHostActionsPopup(playerSession: RoomSessionClientState) {
        const title: TranslationObject = {key: 'strings:roomPage.hostActions.title'}
        const body: TranslationObject = {key: 'strings:roomPage.hostActions.body', options: {username: playerSession.username}}

        const viewProfileAction = async() => {
            const colonistUsername = await getColonistUsername(playerSession.username, playerSession.userId)
            UIViewProfilePopup.init(colonistUsername)
        }
        const kickAction = () => {
            UIRoomUserList.kickPlayer(playerSession)
        }

        const popupOptions: ViewPopupOptions = {
            xButtonCustomContent: {key: 'strings:roomPage.hostActions.viewProfile'},
            checkButtonCustomContent: {key: 'strings:roomPage.hostActions.kick'},
        }

        UIPopupController.create(title, body, kickAction, viewProfileAction, true, popupOptions)
    }

    private static showKickPlayerPopup(playerSession: RoomSessionClientState) {
        const title: TranslationObject = {key: 'strings:roomPage.hostActions.kick'}
        const body: TranslationObject = {key: 'strings:roomPage.hostActions.kickPlayer', options: {username: playerSession.username}}

        const kickAction = () => {
            UIRoomUserList.kickPlayer(playerSession)
        }
        const xAction = () => undefined

        const popupOptions: ViewPopupOptions = {
            xButtonCustomContent: {key: 'strings:colonistCommon.buttons.cancel'},
            checkButtonCustomContent: {key: 'strings:roomPage.hostActions.kick'},
        }

        UIPopupController.create(title, body, kickAction, xAction, true, popupOptions)
    }

    private static async openProfilePage(playerSession: RoomSessionClientState) {
        const colonistUsername = await getColonistUsername(playerSession.username, playerSession.userId)
        UIViewProfilePopup.init(colonistUsername)
    }

    private static getActionIconsToDisplay(roomState: RoomClientState, session: RoomSessionClientState): UserActionIcon[] {
        const actionIcons: UserActionIcon[] = []
        const isCurrentUserSession = this.isItMe(session)
        const isCurrentUserId = this.isItCurrentUserId(session)
        const canGift = !session.isBot && !isCurrentUserSession && !isCurrentUserId && !UIUtilsEjsVariables.isUserOnDiscordMobile

        if(this.canShowAddFriendIcon(session)) {
            actionIcons.push({icon: LobbyImage.AddFriend, action: async() => {
                const colonistUsername = await getColonistUsername(session.username, session.userId)

                if(this.alreadyFriends(colonistUsername)) {
                    notificationController.showNotification(LobbyImage.Friends, {key: 'strings:popups.errors.friendError.alreadyFriends'})
                    return
                }

                if(this.alreadySentFriendRequest(colonistUsername)) {
                    notificationController.showNotification(LobbyImage.Friends, {key: 'strings:popups.errors.friendError.alreadySentFriendRequest'})
                    return
                }

                const eventData: AjaxDataTypes.AnalyticData = {
                    type: AnalyticEventType.Friends,
                    category: AnalyticEventCategory.Room,
                    floatValue: AnalyticEventValue.RoomAddFriendAction,
                }
                void CA.sendAnalyticEvent(eventData)

                void UIUtilsFriends.addFriendAction(colonistUsername)
            }, tooltip: {key: 'strings:game.playerOptionsPopup.addFriend'}})
        }

        if(canGift) {
            const giftButtonClickEventData: AjaxDataTypes.AnalyticData = {
                type: AnalyticEventType.ABTestClickAnalytics,
                category: AnalyticEventCategory.GiftMembershipOverlay,
                floatValue: GiftMembershipMethod.Room,
            }
            actionIcons.push({icon: LobbyImage.StoreGiftMembership, action: () => {
                void CA.sendAnalyticEvent(giftButtonClickEventData)
                doActionIfCanGiftMembership(() => {
                    const giftAction = () => navigateToPage(`/store?username=${session.username}#gift`, true)

                    if(UIUtilsEjsVariables.isUserOnMobileAppOrWeb) {
                        new UIRoomExitConfirmationViewPopup(giftAction)
                        return
                    }
                    giftAction()
                }, session.username)
            }, tooltip: {key: 'strings:game.tooltips.giftMembership', options: {username: session.username}}})
        }

        if(isCurrentUserSession) actionIcons.push({icon: LobbyImage.Pencil, tooltip: {key: 'strings:homePage.header.editProfile'}})
        else if(this.isItMe(roomState.hostSession)) {
            const kickAction = session.isBot
                ? () => UIRoomUserList.kickPlayer(session)
                : () => UIRoomUserList.showKickPlayerPopup(session)

            actionIcons.push({
                icon: LobbyImage.XMark,
                action: kickAction,
                tooltip: {key: 'strings:game.tooltips.kickPlayer', options: {username: session.username}},
            })
        }

        return actionIcons
    }

    private static getUsername(roomState: RoomClientState, session: RoomSessionClientState): string {
        if(this.isItMe(session)) return `${session.username} ${clientI18n.t('strings:roomPage.you')}`
        if(this.isItHost(roomState, session)) return `${session.username} ${clientI18n.t('strings:roomPage.host')}`
        return session.username
    }

    private static togglePlayerHover(player: UIRoomUserView, isDisabled: boolean) {
        if(isDisabled) {
            player.disableHover()
            return
        }
        player.enableHover()
    }

    private static canShowAddFriendIcon(session: RoomSessionClientState): boolean {
        if(session.isBot) return false
        if(UIRoomUserList.isItCurrentUserId(session)) return false

        if(this.alreadyFriends(session.username)) return false
        if(this.alreadySentFriendRequest(session.username)) return false

        return true
    }

    private static alreadyFriends(username: string): boolean {
        return this.friendsData?.friends.some((friend) => friend.username === username) ?? false
    }

    private static alreadySentFriendRequest(username: string): boolean {
        return this.friendsData?.friendRequestsSent.includes(username) ?? false
    }
}
